/**
 * The administrators of the deployment — a table of their own, above the
 * accounts.
 *
 * Two lists and not one filter, because the two answer different questions with
 * different columns. An account is read through what it *holds*: a licence, a
 * plan, an access state, the organizations it belongs to. An administrator
 * holds none of those — the account buys nothing and installs nothing — so
 * every one of those columns would be a dash on every row. What there is to
 * know about an administrator is when they arrived and whether they have ever
 * signed in.
 *
 * Read only. `AIRLOCK_ADMIN_EMAILS` is the one grantor, edited in the Worker's
 * secrets, so this page offers no way to make or unmake an administrator.
 */
import { useEffect, useState } from 'react';
import type React from 'react';
import { color, font } from '../theme/tokens';
import { Row, Table } from '../components/primitives';
import { api } from '../lib/api';
import type { AdministratorList } from '../lib/types';
import { DateCell } from '../components/DateCell';

const COLUMNS = '1.6fr 120px 110px';

export function Administrators(): React.JSX.Element {
  const [list, setList] = useState<AdministratorList | null>(null);
  const load = (): Promise<void> =>
    api
      .get<AdministratorList>('/admin/admins')
      .then(setList)
      .catch(() => setList({ administrators: [], pending: [] }));

  useEffect(() => void load(), []);

  if (!list) return <p style={{ fontSize: 13.5, color: color.inkFaint }}>Reading the administrators…</p>;

  const holdingLicences = list.administrators.filter((one) => one.serial !== null);

  return (
    <>
      <div style={{ fontSize: 15, fontWeight: 600 }}>System administrators</div>
      <p style={{ fontSize: 13.5, color: color.inkSubtle, margin: '8px 0 16px', lineHeight: 1.6, maxWidth: 720 }}>
        They see every tenant of this deployment. They are the addresses in AIRLOCK_ADMIN_EMAILS, and only an edit to that
        secret adds or removes one. An administrator’s account holds no licence and is never billed — it has no Licence,
        no Invoices and no Machines of its own, and it cannot buy one.
      </p>

      <Table columns={COLUMNS} header={['Administrator', 'Since', 'Last seen']}>
        {list.administrators.map((administrator) => (
          <Row key={administrator.id} columns={COLUMNS}>
            <div>
              <div style={{ fontWeight: 500 }}>{administrator.email}</div>
              <div style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint, marginTop: 3 }}>
                {administrator.name ?? 'no name'}
                {administrator.githubLogin ? ` · gh/${administrator.githubLogin}` : ''}
                {administrator.self ? ' · you' : ''}
              </div>
            </div>
            <DateCell at={administrator.createdAt} style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkFaint }} />
            <DateCell at={administrator.lastSeenAt} empty="never" style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkFaint }} />
          </Row>
        ))}
      </Table>

      {list.pending.length > 0 && (
        <p style={{ fontSize: 13, color: color.inkSubtle, margin: '14px 0 0', lineHeight: 1.6 }}>
          AIRLOCK_ADMIN_EMAILS also names{' '}
          <span style={{ fontFamily: font.mono, fontSize: 12.5 }}>{list.pending.join(', ')}</span>, which{' '}
          {list.pending.length === 1 ? 'has' : 'have'} no account here yet. Signing up with that address is all it takes.
        </p>
      )}

      {holdingLicences.length > 0 && (
        <p style={{ fontSize: 13, color: color.warnText, margin: '14px 0 0', lineHeight: 1.6 }}>
          {holdingLicences.map((one) => `${one.email} holds licence #${one.serial}`).join(', ')}. An administrator’s
          account area has no Licence page to read it on — revoke the serial, or take the address out of
          AIRLOCK_ADMIN_EMAILS so the licence has somewhere to be seen.
        </p>
      )}
    </>
  );
}
